/**
 * Child check-in: the rules that decide who may be handed to whom.
 *
 * This is the highest-stakes module in the platform, so the rules live here as
 * pure functions rather than inside a service. Three of Phase 5's acceptance
 * criteria are statements about behaviour — a check-out without the matching
 * code is impossible, an uncleared volunteer cannot be put in a kids room, a
 * room cannot exceed capacity — and a statement about behaviour should be a
 * function you can read in one sitting and a test that fails when it stops
 * being true.
 *
 * Nothing here touches a database, a clock it was not handed, or a random
 * source it was not handed. That is not purity for its own sake: a security
 * code generator you cannot seed is a security code generator you cannot test.
 */

// ---- security codes ---------------------------------------------------------

/**
 * The alphabet a code is drawn from.
 *
 * No I, O, S, Z, 0, 1, 2, 5. A code is read aloud across a noisy foyer and
 * typed by a volunteer holding a toddler, and every pair in that list is one
 * somebody transposes under pressure. Losing eight characters costs less
 * entropy than one misread code costs in trust.
 */
export const CODE_ALPHABET = 'ABCDEFGHJKLMNPQRTUVWXY346789'

/** Three characters from a 28-letter alphabet: 21,952 codes per service. */
export const CODE_LENGTH = 3

/**
 * Generates a code from an injected random source.
 *
 * `random` returns bytes; the caller passes `crypto.getRandomValues`. Modulo
 * bias across 28 symbols is real but tiny, and it is not the property that
 * matters here — a code is a *pickup token scoped to one service*, checked
 * against one child's record, not a secret protecting an account. What matters
 * is that it cannot be guessed by looking at the one before it, which a CSPRNG
 * gives, and that it is not confusable, which the alphabet gives.
 */
export function generateSecurityCode(random: (bytes: Uint8Array) => void): string {
  const bytes = new Uint8Array(CODE_LENGTH)
  random(bytes)
  let code = ''
  for (const byte of bytes) code += CODE_ALPHABET[byte % CODE_ALPHABET.length]
  return code
}

/**
 * Do these codes match?
 *
 * Case-insensitive and whitespace-trimmed, because the volunteer is typing
 * what a parent read off a phone screen. Everything else must match exactly:
 * no prefix matching, no "close enough", no empty string matching an empty
 * stored code. An empty or missing code matches *nothing* — a check-in that
 * somehow has no code is a check-out that has to be resolved by a human, not
 * one that waves everybody through.
 */
export function securityCodeMatches(stored: string | null, supplied: string | null): boolean {
  if (!stored || !supplied) return false
  const a = stored.trim().toUpperCase()
  const b = supplied.trim().toUpperCase()
  if (a.length === 0 || b.length === 0) return false
  return a === b
}

// ---- who may collect a child ------------------------------------------------

export interface Collector {
  personId: string
  /** From `household_members.role`, or an explicit guardian authorization. */
  isHouseholdAdult: boolean
  /** An explicit "this person may collect" row, e.g. a grandparent. */
  isAuthorizedGuardian: boolean
  /** An explicit "this person may NOT collect" row. Outranks everything. */
  isBlocked: boolean
}

export type CollectionDecision =
  { allowed: true } | { allowed: false; reason: string; needsSupervisor: boolean }

/**
 * May this person take this child out of the room?
 *
 * Two independent gates, and both must pass. The code proves they hold the
 * ticket; the relationship proves the ticket is theirs to hold. A code can be
 * photographed off a lock screen, and a parent can be someone a court order
 * says must not collect — neither check substitutes for the other.
 *
 * A block is checked first and cannot be overridden by a supervisor here.
 * "Needs a supervisor" means a human should look at it; a block means the
 * answer is already no, and the software's job is to be unambiguous about that
 * rather than to offer a way round.
 */
export function decideCollection(input: {
  collector: Collector
  storedCode: string | null
  suppliedCode: string | null
}): CollectionDecision {
  if (input.collector.isBlocked) {
    return {
      allowed: false,
      reason: 'This person is not permitted to collect this child. Find a supervisor.',
      needsSupervisor: false,
    }
  }

  if (!securityCodeMatches(input.storedCode, input.suppliedCode)) {
    return {
      allowed: false,
      reason: 'That security code does not match. A supervisor can check them out instead.',
      needsSupervisor: true,
    }
  }

  if (!input.collector.isHouseholdAdult && !input.collector.isAuthorizedGuardian) {
    return {
      allowed: false,
      reason:
        'They have the right code, but they are not listed as an adult in this household or as an authorized guardian. Find a supervisor.',
      needsSupervisor: true,
    }
  }

  return { allowed: true }
}

// ---- volunteer gating -------------------------------------------------------

export const BACKGROUND_CHECK_STATUSES = [
  'not_started',
  'pending',
  'cleared',
  'flagged',
  'expired',
] as const
export type BackgroundCheckStatus = (typeof BACKGROUND_CHECK_STATUSES)[number]

export interface BackgroundCheck {
  status: BackgroundCheckStatus
  /** null ⇒ never expires. A date in the past is expired whatever the status says. */
  expiresAt: Date | null
}

/**
 * Is this person cleared to serve with children, right now?
 *
 * `status === 'cleared'` is not enough on its own: a check cleared in 2023 with
 * a two-year validity is a check that has quietly stopped meaning anything, and
 * a nightly job that flips the status to `expired` is a job that will one day
 * not run. So the expiry is evaluated against the clock every time it is asked,
 * and the stored status is only half the answer.
 *
 * No check at all is the same as no clearance. Absence of evidence is not
 * clearance — that is the entire point of the criterion.
 */
export function isClearedToServeWithChildren(
  check: BackgroundCheck | null | undefined,
  now: Date,
): boolean {
  if (!check) return false
  if (check.status !== 'cleared') return false
  if (check.expiresAt !== null && check.expiresAt <= now) return false
  return true
}

export interface VolunteerAssignmentRefusal {
  reason: string
}

/**
 * May this person be rostered into this location?
 *
 * Only child-serving locations are gated. An adults' prayer room does not need
 * a background check, and requiring one everywhere would train churches to
 * clear people they have not actually checked.
 */
export function refuseVolunteerAssignment(input: {
  locationServesChildren: boolean
  check: BackgroundCheck | null | undefined
  now: Date
}): VolunteerAssignmentRefusal | null {
  if (!input.locationServesChildren) return null
  if (isClearedToServeWithChildren(input.check, input.now)) return null

  const status = input.check?.status
  if (!status || status === 'not_started') {
    return { reason: 'They have no background check on file, so they cannot serve with children.' }
  }
  if (status === 'pending') {
    return {
      reason: 'Their background check has not come back yet, so they cannot serve with children.',
    }
  }
  if (status === 'flagged') {
    return { reason: 'Their background check is flagged. Only an administrator can resolve this.' }
  }
  return { reason: 'Their background check has expired, so they cannot serve with children yet.' }
}

// ---- where a child goes -----------------------------------------------------

/**
 * A room, and who it takes.
 *
 * Age and grade are both here because churches use both and disagree about
 * which. A nursery thinks in months; a youth group thinks in school years. A
 * rule that only understood one would be wrong for half of them.
 */
export interface LocationRule {
  locationId: string
  name: string
  servesChildren: boolean
  /** Inclusive. null ⇒ unbounded on that end. */
  minAgeMonths: number | null
  maxAgeMonths: number | null
  minGrade: number | null
  maxGrade: number | null
  capacity: number | null
  /** null ⇒ anybody; otherwise only people recorded as that gender. */
  gender?: LocationGender | null
  /** "MM-DD": ages are counted as of the last such day, not today. */
  ageAsOf?: string | null
  /** The check-in times it runs at; empty ⇒ all of them. */
  timeIds?: readonly string[]
}

export const LOCATION_GENDERS = ['male', 'female'] as const
export type LocationGender = (typeof LOCATION_GENDERS)[number]

export interface Attendee {
  personId: string
  /** null when the church has never recorded a birthdate. */
  ageMonths: number | null
  grade: number | null
  /**
   * From `household_members.role` and the person's own child flag — the
   * church's answer, not a guess from a birthdate. A household with no
   * recorded birthdays still knows which of its members are the children.
   */
  isChild: boolean
  /** For a room that counts ages as of a fixed day. */
  birthdate?: string | null
  /** Free text from the record; read by `genderOf`. */
  gender?: string | null
}

/** A recorded gender, as a room's rule reads it: "M", "Male", "female"… */
export function genderOf(value: string | null | undefined): LocationGender | null {
  const text = value?.trim().toLowerCase() ?? ''
  if (text === 'm' || text === 'male' || text === 'boy' || text === 'man') return 'male'
  if (text === 'f' || text === 'female' || text === 'girl' || text === 'woman') return 'female'
  return null
}

/**
 * The day ages are counted on for a room with an "age as of" rule: the most
 * recent `MM-DD` on or before today. "Four by 1 September" means the same
 * children all year, until the next 1 September.
 */
export function ageAsOfDate(monthDay: string, now: Date): Date {
  const year = now.getUTCFullYear()
  const candidate = new Date(`${year}-${monthDay}T00:00:00Z`)
  if (Number.isNaN(candidate.getTime())) return now
  return candidate <= now ? candidate : new Date(`${year - 1}-${monthDay}T00:00:00Z`)
}

/** Does this room run at this check-in time? A room with no times runs at all of them. */
export function runsAt(rule: Pick<LocationRule, 'timeIds'>, timeId: string | null | undefined) {
  if (!timeId || !rule.timeIds || rule.timeIds.length === 0) return true
  return rule.timeIds.includes(timeId)
}

/** Whole months between two dates — a nursery's unit, not a rounded year. */
export function ageInMonths(birthdate: string, now: Date): number | null {
  const born = new Date(`${birthdate}T00:00:00Z`)
  if (Number.isNaN(born.getTime())) return null
  let months =
    (now.getUTCFullYear() - born.getUTCFullYear()) * 12 + (now.getUTCMonth() - born.getUTCMonth())
  // Not yet reached the day of the month, so the last month is incomplete.
  if (now.getUTCDate() < born.getUTCDate()) months -= 1
  return months < 0 ? null : months
}

function withinRange(value: number | null, min: number | null, max: number | null): boolean {
  // No rule on this axis means the axis does not exclude anybody.
  if (min === null && max === null) return true
  // A rule exists and we do not know the value: that is not a match. Guessing
  // a room for a child whose age nobody recorded is how a two-year-old ends up
  // with the nine-year-olds.
  if (value === null) return false
  if (min !== null && value < min) return false
  if (max !== null && value > max) return false
  return true
}

/**
 * A room with no age or grade rules takes anybody *of its kind* — and that is
 * the trap `serves_children` closes.
 *
 * An adults' prayer room has no bands on it, so every range check passes and
 * without this a nine-month-old is offered it alongside the nursery. The demo
 * seed showed exactly that. The flag is not only a volunteer gate: it says who
 * the room is *for*, and a room for adults is not somewhere a child goes.
 *
 * It reads both ways. A child is only offered child-serving rooms; an adult is
 * never offered the nursery, which stops a parent being checked in to their
 * toddler's room by a mis-tap.
 */
export function locationFits(rule: LocationRule, attendee: Attendee, now = new Date()): boolean {
  if (rule.servesChildren !== attendee.isChild) return false
  // A gender rule with no recorded gender is not a match, for the same
  // reason an age rule with no birthdate is not.
  if (rule.gender && genderOf(attendee.gender) !== rule.gender) return false
  const ageMonths =
    rule.ageAsOf && attendee.birthdate
      ? ageInMonths(attendee.birthdate, ageAsOfDate(rule.ageAsOf, now))
      : attendee.ageMonths
  return (
    withinRange(ageMonths, rule.minAgeMonths, rule.maxAgeMonths) &&
    withinRange(attendee.grade, rule.minGrade, rule.maxGrade)
  )
}

/**
 * The rooms this child could go in, best first.
 *
 * "Best" is the narrowest rule that still fits — a room specified as 24–35
 * months beats one specified as 0–60, because somebody wrote the tighter rule
 * on purpose. Ties break on name so the list is stable between renders and a
 * volunteer does not see the buttons move under their thumb.
 */
export function eligibleLocations(
  rules: LocationRule[],
  attendee: Attendee,
  now = new Date(),
): LocationRule[] {
  const span = (rule: LocationRule) => {
    const ageSpan =
      rule.minAgeMonths === null && rule.maxAgeMonths === null
        ? Number.MAX_SAFE_INTEGER
        : (rule.maxAgeMonths ?? 1200) - (rule.minAgeMonths ?? 0)
    const gradeSpan =
      rule.minGrade === null && rule.maxGrade === null
        ? Number.MAX_SAFE_INTEGER
        : (rule.maxGrade ?? 13) - (rule.minGrade ?? 0)
    return Math.min(ageSpan, gradeSpan)
  }

  return rules
    .filter((rule) => locationFits(rule, attendee, now))
    .sort((a, b) => span(a) - span(b) || a.name.localeCompare(b.name))
}

export type CapacityState =
  { state: 'open'; remaining: number | null } | { state: 'full'; remaining: 0 }

/**
 * Room capacity is a *hard* stop, not a warning.
 *
 * A ratio is a legal requirement in most places, and a check-in screen that
 * lets a volunteer click past it is a check-in screen that will be clicked
 * past on the busiest morning of the year. The service refuses; the screen
 * says which other rooms are open.
 */
export function capacityOf(rule: LocationRule, checkedIn: number): CapacityState {
  if (rule.capacity === null) return { state: 'open', remaining: null }
  const remaining = rule.capacity - checkedIn
  return remaining > 0 ? { state: 'open', remaining } : { state: 'full', remaining: 0 }
}

/**
 * Enough volunteers for the children in the room?
 *
 * A warning, not a stop — unlike capacity. The second leader may be walking
 * in from the car park, and refusing a child at the door is not how a ratio
 * gets met. The room screen shows it so a coordinator sends someone.
 */
export function ratioOf(
  childrenPerVolunteer: number | null,
  children: number,
  volunteers: number,
): { needed: number; short: boolean } | null {
  if (childrenPerVolunteer === null || childrenPerVolunteer <= 0) return null
  const needed = children === 0 ? 0 : Math.ceil(children / childrenPerVolunteer)
  return { needed, short: volunteers < needed }
}

// ---- service times -------------------------------------------------------------

/** A weekly service check-in runs for. Times are local "HH:MM". */
export interface CheckinTimeRule {
  id: string
  name: string
  campusId: string | null
  /** 0 = Sunday. */
  dayOfWeek: number
  startsAt: string
  endsAt: string
  opensMinutesBefore: number
}

const CLOCK = /^([01][0-9]|2[0-3]):([0-5][0-9])$/

export const isClock = (value: string): boolean => CLOCK.test(value)

const minutesOf = (clock: string): number =>
  Number(clock.slice(0, 2)) * 60 + Number(clock.slice(3, 5))

/** The local weekday, date and minute of an instant in a time zone. */
export function localClock(
  now: Date,
  timeZone: string,
): { date: string; dayOfWeek: number; minutes: number } {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    weekday: 'short',
  }).formatToParts(now)
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? ''
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
  return {
    date: `${get('year')}-${get('month')}-${get('day')}`,
    dayOfWeek: days.indexOf(get('weekday')),
    minutes: Number(get('hour')) * 60 + Number(get('minute')),
  }
}

/**
 * The service a station is checking in to right now: the one whose window —
 * from `opensMinutesBefore` its start until its end — contains this minute,
 * on its own weekday, in the church's time zone. Two windows overlapping (the
 * 9:00 still open when the 11:00 opens) go to the earlier service until it
 * closes: a family arriving at 10:20 for the 9:00 is late, not early.
 *
 * Null when nothing is running — the station then offers every room, as it
 * did before rooms had times.
 */
export function currentCheckinTime<T extends CheckinTimeRule>(
  times: readonly T[],
  now: Date,
  timeZone: string,
  campusId?: string | null,
): T | null {
  const clock = localClock(now, timeZone)
  const open = times.filter((time) => {
    if (campusId && time.campusId && time.campusId !== campusId) return false
    if (time.dayOfWeek !== clock.dayOfWeek) return false
    const start = minutesOf(time.startsAt) - time.opensMinutesBefore
    return clock.minutes >= start && clock.minutes <= minutesOf(time.endsAt)
  })
  open.sort((a, b) => minutesOf(a.startsAt) - minutesOf(b.startsAt))
  return open[0] ?? null
}

export const WEEKDAY_NAMES = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
] as const

/**
 * Digits as they are typed on a kiosk keypad, shown the way a phone number
 * reads: "(555) 123-4567" while it is ten digits or fewer (North American),
 * plain digits beyond. Only the display changes; the search takes the digits.
 */
export function formatKeypadPhone(digits: string): string {
  const d = digits.replace(/\D/g, '')
  if (d.length === 0 || d.length > 10) return d
  if (d.length <= 3) return d.length === 3 ? `(${d}) ` : `(${d}`
  if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`
}
